import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Authenticated, Refine } from '@refinedev/core';
import {
  ErrorComponent,
  ThemedLayout,
  ThemedSider,
  ThemedTitle,
  useNotificationProvider,
} from '@refinedev/antd';
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router';
import {
  App as AntdApp,
  ConfigProvider,
  Layout,
  Select,
  Switch,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import zhTW from 'antd/locale/zh_TW';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router';
import {
  UserOutlined,
  CrownOutlined,
  AppstoreOutlined,
  MobileOutlined,
  CloudUploadOutlined,
  SunOutlined,
  MoonOutlined,
  AuditOutlined,
  CloudOutlined,
  StopOutlined,
  DashboardOutlined,
  GlobalOutlined,
  TeamOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  BugOutlined,
  SettingOutlined,
  UserSwitchOutlined,
  RadarChartOutlined,
  GiftOutlined,
  AccountBookOutlined,
  FundOutlined,
  BarChartOutlined,
  NotificationOutlined,
} from '@ant-design/icons';

import { THEME_KEY } from './utils/constants';

import '@refinedev/antd/dist/reset.css';

import { authProvider } from './providers/authProvider';
import { dataProvider } from './providers/dataProvider';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { LoginPage } from './pages/login';
import { ForcePasswordPage } from './pages/force-password';
import { LandingPage } from './pages/landing';
import { UserList } from './pages/users';
import { AdminList } from './pages/admins';
import { NodeList } from './pages/nodes';
import { TierList } from './pages/tiers';
import PromoCodeList from './pages/promo-codes';
import { AccountingOverviewPage } from './pages/accounting';
import { AccountingGrantsPage } from './pages/accounting/grants';
import { AccountingUsagePage } from './pages/accounting/usage';
import { VlessPage } from './pages/vless';
import { AuditRulesPage } from './pages/audit-rules';
import { SingboxSyncAllowlistPage } from './pages/sing-box-sync-allowlist';
import { DomainPoolList } from './pages/domains';
import { ApiDomainPoolPage } from './pages/api-domains';
// `LogList` (API 日誌) page retired in Sprint 26 W1-5 / E6 A1 together with
// the `vpn_api_log` table — operators use the Client / Worker / Audit log
// pages for live troubleshooting.
import { VersionList } from './pages/versions';
import { AnnouncementListPage } from './pages/announcements';
import { AnnouncementEditorPage } from './pages/announcements/editor';
import { ClientLogList } from './pages/client-logs';
import { WorkerLogList } from './pages/worker-logs';
import { OtaList } from './pages/ota';
import { AuditList } from './pages/audit';
import { DashboardPage } from './pages/dashboard';
import { OperationsPage } from './pages/operations';
import { SubscriptionPlansPage } from './pages/subscriptions/plans';
import { PaymentOrdersPage } from './pages/subscriptions/payments';
import { MemberSubscriptionsPage } from './pages/subscriptions/members';
import { SubscriptionOperationsPage } from './pages/subscriptions/manual';
import { SubscriptionAuditPage } from './pages/subscriptions/audit';
import { BrandList } from './pages/brands';
import { BenefitConfigPage } from './pages/benefit-config';
import { ContentReachConfigPage } from './pages/content-reach-config';
import { DecoyConnectionsPage } from './pages/decoy-connections';
import { MemberDevicesPage } from './pages/member-devices';
import { MemberEmailVerificationsPage } from './pages/member-email-verifications';
import { MemberEmailBlacklistPage } from './pages/member-email-blacklist';
import { MemberAuthRateLimitsPage } from './pages/member-auth-rate-limits';
import { NodeBrandMatrixPage } from './pages/node-brand-matrix';
import {
  AntiDetectionTelemetryPage,
  AntiDetectionSignalDrillDown,
  AntiDetectionTelemetryIndex,
} from './pages/anti-detection-telemetry';
import { AntiDetectionCorrelationPage } from './pages/anti-detection-telemetry/correlation';
import {
  BRAND_CATALOG_CHANGE_EVENT,
  getBrandOptions,
  getBrandLabel,
  tokenManager,
} from './utils/tokenManager';
import { authFetch } from './utils/api';
import {
  ViewScopeProvider,
  useViewScope,
  useSyncViewScopeSnapshot,
  ALL_SCOPE,
  type ViewScope,
} from './utils/viewScope';

const AuthenticatedDefaultRoute = () => {
  if (tokenManager.mustChangePasswordRequired()) {
    return <Navigate to="/force-password" replace />;
  }

  return <NavigateToResource resource="dashboard" />;
};

const PasswordRotationGuard = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const mustChangePassword = tokenManager.mustChangePasswordRequired();

  if (mustChangePassword && location.pathname !== '/force-password') {
    return <Navigate to="/force-password" replace />;
  }

  if (!mustChangePassword && location.pathname === '/force-password') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * 檢視範圍切換器（去品牌化 M1）— 全站唯一的品牌切換入口。
 *
 * 只改「讀取視角」：切換後透過 ViewScope context 觸發各頁 refetch，
 * 不打 switch-brand、不換 JWT、不 reload。寫入絕不繼承此檢視範圍。
 */
const ViewScopeSwitcher = () => {
  const [brandOptions, setBrandOptions] = useState(() => getBrandOptions());
  const { scope, setScope } = useViewScope();
  const hasToken = tokenManager.hasToken();
  // 過渡期權限：非全品牌管理員只能檢視自己被授權的品牌（沿 brandScope 判斷）。
  const isAllBrandAdmin = tokenManager.isAllBrandAdmin();

  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;
    authFetch('/api/admin/brands')
      .then((response) => response.json())
      .then((result) => {
        if (cancelled || result.code !== 0 || !Array.isArray(result.data)) {
          return;
        }
        tokenManager.setBrandCatalog(result.data);
        setBrandOptions(getBrandOptions());
      })
      .catch(() => {
        // Keep seeded options if brand registry is unreachable.
      });
    return () => {
      cancelled = true;
    };
  }, [hasToken]);

  useEffect(() => {
    const syncBrandOptions = () => {
      setBrandOptions(getBrandOptions());
    };
    window.addEventListener(BRAND_CATALOG_CHANGE_EVENT, syncBrandOptions);
    return () => {
      window.removeEventListener(BRAND_CATALOG_CHANGE_EVENT, syncBrandOptions);
    };
  }, []);

  if (!hasToken) return null;

  const allowedBrands = tokenManager.getAllowedBrands();
  const brandChoices = brandOptions.filter((option) =>
    allowedBrands.includes(option.value),
  );
  // 全品牌管理員才提供「全部品牌（彙總）」聚合選項。
  const options = [
    ...(isAllBrandAdmin
      ? [{ label: '全部品牌（彙總）', value: ALL_SCOPE }]
      : []),
    ...brandChoices.map((o) => ({ label: o.label, value: o.value })),
  ];

  return (
    <Space size={8} wrap>
      <Typography.Text type="secondary">檢視範圍</Typography.Text>
      <Select
        aria-label="檢視範圍"
        data-testid="view-scope-switcher"
        value={scope}
        style={{ width: 176 }}
        disabled={options.length <= 1}
        options={options}
        onChange={(next) => setScope(next as ViewScope)}
      />
      <Tag color={scope === ALL_SCOPE ? 'gold' : 'blue'}>
        {scope === ALL_SCOPE ? '彙總' : getBrandLabel(scope)}
      </Tag>
    </Space>
  );
};

/**
 * 把 ViewScope context 目前值鏡射到 module snapshot，供 authFetch（React 樹外）讀取。
 */
const ViewScopeSnapshotBridge = () => {
  useSyncViewScopeSnapshot();
  return null;
};

/**
 * VPN 管理後台 — Refine v5 主程式
 */
function App() {
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem(THEME_KEY) === 'dark',
  );
  const showSystemSettings = tokenManager.hasToken() && tokenManager.isAllBrandAdmin();

  return (
    <BrowserRouter>
      <ConfigProvider
        locale={zhTW}
        theme={{
          algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
          token: {
            colorPrimary: '#1677ff',
            borderRadius: 6,
          },
          components: {
            Form: {
              labelColonMarginInlineEnd: 8,
              itemMarginBottom: 16,
              verticalLabelPadding: '0 0 4px',
            },
            Table: {
              cellPaddingBlock: 12,
              cellPaddingInline: 12,
              headerSplitColor: 'transparent',
            },
            Input: {
              paddingBlock: 6,
            },
            InputNumber: {
              paddingBlock: 6,
            },
            Select: {
              paddingSM: 8,
            },
            Modal: {
              titleFontSize: 16,
            },
            Card: {
              paddingLG: 20,
            },
          },
        }}
      >
        <AntdApp>
          <Refine
            routerProvider={routerProvider}
            authProvider={authProvider}
            dataProvider={dataProvider}
            notificationProvider={useNotificationProvider}
            resources={[
              // ── 群一【日常營運】兩種角色皆可見，品牌 admin 主場（IA §3.2）──
              {
                name: 'daily-ops',
                meta: {
                  label: '日常營運',
                  icon: <TeamOutlined />,
                },
              },
              {
                name: 'dashboard',
                list: '/dashboard',
                meta: {
                  label: '營運總覽',
                  icon: <DashboardOutlined />,
                  parent: 'daily-ops',
                },
              },
              {
                name: 'operations',
                list: '/operations',
                meta: {
                  label: '營運數據',
                  icon: <BarChartOutlined />,
                  parent: 'daily-ops',
                },
              },
              {
                name: 'users',
                list: '/users',
                meta: {
                  label: '會員管理',
                  icon: <UserOutlined />,
                  parent: 'daily-ops',
                },
              },
              {
                name: 'member-devices',
                list: '/member-devices',
                meta: {
                  label: '會員裝置關聯',
                  icon: <MobileOutlined />,
                  parent: 'daily-ops',
                },
              },
              { name: 'member-email-verifications', list: '/member-email-verifications', meta: { label: '會員驗證碼查詢', icon: <MobileOutlined />, parent: 'daily-ops' } },
              { name: 'member-email-blacklist', list: '/member-email-blacklist', meta: { label: '會員 Email 黑名單', icon: <SafetyCertificateOutlined />, parent: 'daily-ops' } },
              { name: 'member-auth-rate-limits', list: '/member-auth-rate-limits', meta: { label: '會員認證限流', icon: <StopOutlined />, parent: 'daily-ops' } },
              {
                name: 'tiers',
                list: '/tiers',
                meta: {
                  label: '等級方案',
                  icon: <CrownOutlined />,
                  parent: 'daily-ops',
                },
              },
              {
                name: 'promo-codes',
                list: '/promo-codes',
                meta: {
                  // 下一階段合併「碼管理｜兌換/發放紀錄」為單頁 Tabs（§3.2 item 4）
                  label: '兌換與發放',
                  icon: <GiftOutlined />,
                  parent: 'daily-ops',
                },
              },
              { name: 'subscription-commerce', meta: { label: '訂閱與支付', icon: <AccountBookOutlined />, parent: 'daily-ops' } },
              { name: 'subscription-plans', list: '/subscription-plans', meta: { label: '方案與權益', icon: <CrownOutlined />, parent: 'subscription-commerce' } },
              { name: 'payment-orders', list: '/payment-orders', meta: { label: '支付訂單與證據', icon: <FundOutlined />, parent: 'subscription-commerce' } },
              { name: 'member-subscriptions', list: '/member-subscriptions', meta: { label: '會員訂閱與用量', icon: <UserOutlined />, parent: 'subscription-commerce' } },
              { name: 'subscription-operations', list: '/subscription-operations', meta: { label: '人工訂閱操作', icon: <GiftOutlined />, parent: 'subscription-commerce' } },
              { name: 'subscription-audit-logs', list: '/subscription-audit-logs', meta: { label: '訂閱審計紀錄', icon: <AccountBookOutlined />, parent: 'subscription-commerce' } },
              // 帳務與用量（子群；下一階段合併三頁為單頁 Tabs，目前保留三頁 §3.2 item 5）
              {
                name: 'accounting',
                meta: {
                  label: '帳務與用量',
                  icon: <AccountBookOutlined />,
                  parent: 'daily-ops',
                },
              },
              {
                name: 'accounting-overview',
                list: '/accounting',
                meta: {
                  label: '帳務總覽',
                  icon: <FundOutlined />,
                  parent: 'accounting',
                },
              },
              {
                name: 'accounting-grants',
                list: '/accounting/grants',
                meta: {
                  label: '兌換／發放紀錄',
                  icon: <GiftOutlined />,
                  parent: 'accounting',
                },
              },
              {
                name: 'accounting-usage',
                list: '/accounting/usage',
                meta: {
                  label: '流量使用明細',
                  icon: <BarChartOutlined />,
                  parent: 'accounting',
                },
              },
              {
                name: 'client-logs',
                list: '/client-logs',
                meta: {
                  // 保留 raw-export 授權 gate（§3.2 item 6）
                  label: '客戶端日誌',
                  icon: <MobileOutlined />,
                  parent: 'daily-ops',
                },
              },
              // `api-logs` resource retired in Sprint 26 W1-5 / E6 A1 along
              // with the `vpn_api_log` table.  Operator troubleshooting moved
              // to client-logs / worker-logs / audit-logs.

              // ── 群二【共享基礎設施】全域 admin 可寫；品牌 admin 唯讀可見（非隱藏，§3.2）──
              {
                name: 'shared-infra',
                meta: {
                  label: '共享基礎設施',
                  icon: <SafetyCertificateOutlined />,
                },
              },
              {
                name: 'nodes',
                list: '/nodes',
                meta: {
                  label: '節點與可見性',
                  icon: <GlobalOutlined />,
                  parent: 'shared-infra',
                },
              },
              {
                // VPN-80 IA 落點：節點×品牌×方案可用性矩陣（P1 stub，內容下一階段填，見 §5.4）
                name: 'node-brand-matrix',
                list: '/node-brand-matrix',
                meta: {
                  label: '節點×品牌可用性矩陣',
                  icon: <AppstoreOutlined />,
                  parent: 'shared-infra',
                },
              },
              {
                name: 'vless',
                list: '/vless',
                meta: {
                  label: 'VLESS / 優選 IP',
                  icon: <CloudOutlined />,
                  parent: 'shared-infra',
                },
              },
              {
                name: 'audit-rules',
                list: '/audit-rules',
                meta: {
                  label: '節點封鎖規則',
                  icon: <StopOutlined />,
                  parent: 'shared-infra',
                },
              },
              {
                name: 'sing-box-sync-allowlist',
                list: '/sing-box-sync-allowlist',
                meta: {
                  label: '節點同步 IP 白名單',
                  icon: <GlobalOutlined />,
                  parent: 'shared-infra',
                },
              },
              {
                name: 'domains',
                list: '/domains',
                meta: {
                  label: '域名池',
                  icon: <GlobalOutlined />,
                  parent: 'shared-infra',
                },
              },
              {
                name: 'api-domains',
                list: '/api-domains',
                meta: {
                  label: 'API 動態域名',
                  icon: <GlobalOutlined />,
                  parent: 'shared-infra',
                },
              },
              // ── 群三【應用發布】版本/OTA 依品牌分流，品牌 admin 仍可見自己品牌（§3.2）──
              {
                name: 'app-delivery',
                meta: {
                  label: '應用發布',
                  icon: <RocketOutlined />,
                },
              },
              {
                name: 'versions',
                list: '/versions',
                meta: {
                  label: '版本管理',
                  icon: <AppstoreOutlined />,
                  parent: 'app-delivery',
                },
              },
              {
                name: 'ota-updates',
                list: '/ota',
                meta: {
                  label: 'OTA 發布',
                  icon: <CloudUploadOutlined />,
                  parent: 'app-delivery',
                },
              },
              ...(tokenManager.getAllowedBrands().includes('wudi_star') ? [{
                name: 'announcements',
                list: '/announcements',
                meta: { label: '公告管理', icon: <NotificationOutlined />, parent: 'app-delivery' },
              }] : []),
              // 福利設定是依品牌隔離的 operator 功能；品牌 admin 也必須能進入。
              {
                name: 'benefit-config',
                list: '/benefit-config',
                meta: { label: '福利設定', icon: <GiftOutlined /> },
              },
              // ── 群四【系統與進階】僅全域 admin 可見（沿用 showSystemSettings gate，§3.2）──
              // 代號（AR-Q7 / S23-G + S25-M）移到 meta.tooltip，不再污染 nav label（§3.3）。
              // 系統日誌 / 反審查遙測 的 Tab 合併由下一階段做，目前保留個別 resource。
              ...(showSystemSettings
                ? [
                    {
                      name: 'system-advanced',
                      meta: {
                        label: '系統與進階',
                        icon: <SettingOutlined />,
                      },
                    },
                    {
                      name: 'brands',
                      list: '/brands',
                      meta: {
                        label: '品牌註冊表',
                        icon: <AppstoreOutlined />,
                        parent: 'system-advanced',
                      },
                    },
                    {
                      name: 'content-reach-config',
                      list: '/content-reach-config',
                      meta: {
                        label: '內容可達設定',
                        icon: <GlobalOutlined />,
                        parent: 'system-advanced',
                      },
                    },
                    {
                      name: 'admins',
                      list: '/admins',
                      meta: {
                        label: '管理員與權限',
                        icon: <UserSwitchOutlined />,
                        parent: 'system-advanced',
                      },
                    },
                    {
                      name: 'worker-logs',
                      list: '/worker-logs',
                      meta: {
                        label: 'Worker 日誌',
                        icon: <BugOutlined />,
                        parent: 'system-advanced',
                      },
                    },
                    {
                      name: 'audit-logs',
                      list: '/audit',
                      meta: {
                        label: '審計日誌',
                        icon: <AuditOutlined />,
                        parent: 'system-advanced',
                      },
                    },
                    {
                      name: 'decoy-connections',
                      list: '/decoy-connections',
                      meta: {
                        label: '蜜罐紀錄',
                        tooltip: 'AR-Q7',
                        icon: <BugOutlined />,
                        parent: 'system-advanced',
                      },
                    },
                    {
                      name: 'anti-detection-telemetry',
                      list: '/admin/anti-detection-telemetry',
                      meta: {
                        label: '反偵測信號',
                        tooltip: 'S23-G + S25-M cross-correlation',
                        icon: <RadarChartOutlined />,
                        parent: 'system-advanced',
                      },
                    },
                  ]
                : []),
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              disableTelemetry: true,
              projectId: 'rpvpn-bo',
              title: {
                text: '多品牌後台',
                icon: '',
              },
            }}
          >
            <ViewScopeProvider>
            <ViewScopeSnapshotBridge />
            <Routes>
              {/* 偽裝首頁 — 公開路由 */}
              <Route
                path="/"
                element={
                  <Authenticated
                    key="landing-auth-check"
                    fallback={<LandingPage />}
                  >
                    <AuthenticatedDefaultRoute />
                  </Authenticated>
                }
              />

              {/* /login 重導向到偽裝首頁 */}
              <Route path="/login" element={<Navigate to="/" replace />} />

              {/* 真正的登入頁：/manage */}
              <Route
                path="/manage"
                element={
                  <Authenticated
                    key="manage-auth-check"
                    fallback={<LoginPage />}
                  >
                    <AuthenticatedDefaultRoute />
                  </Authenticated>
                }
              />

              <Route
                path="/force-password"
                element={
                  <Authenticated
                    key="force-password-auth-check"
                    fallback={<CatchAllNavigate to="/manage" />}
                  >
                    <PasswordRotationGuard>
                      <ForcePasswordPage />
                    </PasswordRotationGuard>
                  </Authenticated>
                }
              />

              {/* 需要認證的路由 */}
              <Route
                element={
                  <Authenticated
                    key="authenticated-layout"
                    fallback={<CatchAllNavigate to="/manage" />}
                  >
                    <PasswordRotationGuard>
                      <ThemedLayout
                        Header={() => (
                          <Layout.Header
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              padding: '0 24px',
                              height: 64,
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                            }}
                          >
                            <Space>
                              <ViewScopeSwitcher />
                              <Switch
                                checkedChildren={<MoonOutlined />}
                                unCheckedChildren={<SunOutlined />}
                                checked={isDark}
                                onChange={(checked) => {
                                  setIsDark(checked);
                                  localStorage.setItem(
                                    THEME_KEY,
                                    checked ? 'dark' : 'light',
                                  );
                                }}
                              />
                            </Space>
                          </Layout.Header>
                        )}
                        Title={({ collapsed }) => (
                          <ThemedTitle
                            collapsed={collapsed}
                            text="多品牌後台"
                          />
                        )}
                        Sider={(props) => <ThemedSider {...props} fixed />}
                      >
                        <Outlet />
                      </ThemedLayout>
                    </PasswordRotationGuard>
                  </Authenticated>
                }
              >
                <Route path="/dashboard" element={<RouteErrorBoundary><DashboardPage /></RouteErrorBoundary>} />
                <Route path="/subscription-plans" element={<RouteErrorBoundary><SubscriptionPlansPage /></RouteErrorBoundary>} />
                <Route path="/payment-orders" element={<RouteErrorBoundary><PaymentOrdersPage /></RouteErrorBoundary>} />
                <Route path="/member-subscriptions" element={<RouteErrorBoundary><MemberSubscriptionsPage /></RouteErrorBoundary>} />
                <Route path="/subscription-operations" element={<RouteErrorBoundary><SubscriptionOperationsPage /></RouteErrorBoundary>} />
                <Route path="/subscription-audit-logs" element={<RouteErrorBoundary><SubscriptionAuditPage /></RouteErrorBoundary>} />
                <Route path="/operations" element={<RouteErrorBoundary><OperationsPage /></RouteErrorBoundary>} />
                <Route path="/users" element={<RouteErrorBoundary><UserList /></RouteErrorBoundary>} />
                <Route path="/member-devices" element={<RouteErrorBoundary><MemberDevicesPage /></RouteErrorBoundary>} />
                <Route path="/member-email-verifications" element={<RouteErrorBoundary><MemberEmailVerificationsPage /></RouteErrorBoundary>} />
                <Route path="/member-email-blacklist" element={<RouteErrorBoundary><MemberEmailBlacklistPage /></RouteErrorBoundary>} />
                <Route path="/member-auth-rate-limits" element={<RouteErrorBoundary><MemberAuthRateLimitsPage /></RouteErrorBoundary>} />
                <Route path="/admins" element={<RouteErrorBoundary><AdminList /></RouteErrorBoundary>} />
                <Route path="/brands" element={<RouteErrorBoundary><BrandList /></RouteErrorBoundary>} />
                <Route path="/benefit-config" element={<RouteErrorBoundary><BenefitConfigPage /></RouteErrorBoundary>} />
                <Route path="/content-reach-config" element={<RouteErrorBoundary><ContentReachConfigPage /></RouteErrorBoundary>} />
                <Route path="/nodes" element={<RouteErrorBoundary><NodeList /></RouteErrorBoundary>} />
                <Route path="/node-brand-matrix" element={<RouteErrorBoundary><NodeBrandMatrixPage /></RouteErrorBoundary>} />
                <Route path="/ip" element={<Navigate to="/nodes" replace />} />
                <Route path="/domains" element={<RouteErrorBoundary><DomainPoolList /></RouteErrorBoundary>} />
                <Route path="/api-domains" element={<RouteErrorBoundary><ApiDomainPoolPage /></RouteErrorBoundary>} />
                <Route path="/tiers" element={<RouteErrorBoundary><TierList /></RouteErrorBoundary>} />
                <Route path="/promo-codes" element={<RouteErrorBoundary><PromoCodeList /></RouteErrorBoundary>} />
                <Route path="/accounting" element={<RouteErrorBoundary><AccountingOverviewPage /></RouteErrorBoundary>} />
                <Route path="/accounting/grants" element={<RouteErrorBoundary><AccountingGrantsPage /></RouteErrorBoundary>} />
                <Route path="/accounting/usage" element={<RouteErrorBoundary><AccountingUsagePage /></RouteErrorBoundary>} />
                <Route path="/vless" element={<RouteErrorBoundary><VlessPage /></RouteErrorBoundary>} />
                <Route path="/audit-rules" element={<RouteErrorBoundary><AuditRulesPage /></RouteErrorBoundary>} />
                <Route path="/sing-box-sync-allowlist" element={<RouteErrorBoundary><SingboxSyncAllowlistPage /></RouteErrorBoundary>} />
                <Route path="/stats" element={<Navigate to="/dashboard" replace />} />
                <Route path="/protocol-health" element={<Navigate to="/dashboard" replace />} />
                {/* /logs route retired in Sprint 26 W1-5 / E6 A1 — see
                    docs/architecture/observability-tables.md.  Old bookmarks
                    redirect to the client-log page which carries the same
                    forensic intent. */}
                <Route path="/logs" element={<Navigate to="/client-logs" replace />} />
                <Route path="/versions" element={<RouteErrorBoundary><VersionList /></RouteErrorBoundary>} />
                <Route path="/announcements" element={<RouteErrorBoundary><AnnouncementListPage /></RouteErrorBoundary>} />
                <Route path="/announcements/new" element={<RouteErrorBoundary><AnnouncementEditorPage /></RouteErrorBoundary>} />
                <Route path="/announcements/:id/edit" element={<RouteErrorBoundary><AnnouncementEditorPage /></RouteErrorBoundary>} />
                <Route path="/client-logs" element={<RouteErrorBoundary><ClientLogList /></RouteErrorBoundary>} />
                <Route path="/worker-logs" element={<RouteErrorBoundary><WorkerLogList /></RouteErrorBoundary>} />
                <Route path="/ota" element={<RouteErrorBoundary><OtaList /></RouteErrorBoundary>} />
                <Route path="/audit" element={<RouteErrorBoundary><AuditList /></RouteErrorBoundary>} />
                <Route path="/decoy-connections" element={<RouteErrorBoundary><DecoyConnectionsPage /></RouteErrorBoundary>} />
                <Route path="/admin/anti-detection-telemetry" element={<RouteErrorBoundary><AntiDetectionTelemetryPage /></RouteErrorBoundary>} />
                <Route path="/admin/anti-detection-telemetry/:signalKey" element={<RouteErrorBoundary><AntiDetectionSignalDrillDown /></RouteErrorBoundary>} />
                <Route path="/anti-detection-telemetry" element={<RouteErrorBoundary><AntiDetectionTelemetryIndex /></RouteErrorBoundary>} />
                <Route path="/anti-detection-telemetry/correlation" element={<RouteErrorBoundary><AntiDetectionCorrelationPage /></RouteErrorBoundary>} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<ErrorComponent />} />
            </Routes>
            </ViewScopeProvider>

            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}

export default App;
